import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import { useSelectedCustomerId } from '@/ui/Navbar/Navbar';
import { useUserInfo } from '@/features/_data/useUserInfo';
import {
  IconArrowLeft,
  IconArrowRight,
  IconEdit,
  IconLink,
  IconTrash,
  IconBook,
  IconView,
} from '@/icons';
import { DropFile } from '@/ui/DropFile/DropFile';
import { Flex } from '@/ui/Flex/Flex';
import { T } from '@/ui/Text/Text';
import { triggerFileUploadDialog } from '@/utils/triggerFileUploadDialog';
import { SensorType } from 'src/types/SensorType';

import { FrontendSlide } from '@/types/SlideType';
import { SlideVideoOptions } from '@/types/SlideVideoOptions';
import { useToast } from '@/ui/Toast/Toast';
import { KebapMenu, MenuItem } from '@/ui/KebapMenu/KebapMenu';
import { MagicModel } from '@/types/MagicModel';
import { EnterUrlModal } from './EnterUrlModal';
import { SensorMappingSidebar } from './SensorMappingSidebar/SensorMappingSidebar';
import { SlideLibrary } from './SlidesLibrary/SlideLibrary';

export const StyledSlideWrapper = styled.div<{
  readonly isSidebarOpen: boolean;
  readonly isHomeSlide?: boolean;
  readonly isTrihowPad?: boolean;
}>`
  display: flex;
  height: 100%;
  ${({ isHomeSlide, isTrihowPad }) =>
    !isHomeSlide &&
    !isTrihowPad &&
    css`
      border: solid 2px #f6f6f6;
    `}
  border-radius: 8px;
  padding: 16px;
  ${({ isSidebarOpen }) =>
    isSidebarOpen &&
    css`
      outline: solid 3px #00ff6a;
    `}
`;

const StyledTopSlideBar = styled(Flex)`
  position: relative;
  width: 100%;
  height: 100%;
`;

const StyledSlideName = styled.div<{
  readonly hasName: boolean;
  readonly slide: boolean;
}>`
  width: calc(100% - 32px);
  margin: ${({ slide }) => (slide ? '0' : '18px')};

  input {
    border: ${({ hasName }) =>
      hasName ? 'solid 1px transparent' : 'dashed 1px #ccc'};
    background: none;
    outline: none;
    padding: 2px 4px;
    width: 100%;

    font-weight: 600;
    font-size: 14px;
    line-height: 150%;
    color: ${({ theme }) => theme.color('#1B1A1F')};

    &:focus {
      border: solid 1px #ccc;
      border-radius: 4px;
    }
  }
`;

const StyledDropzone = styled.div<{ hasSlide: boolean }>`
  cursor: pointer;
  display: grid;
  width: 100%;
  height: 100%;
  grid-template-columns: ${({ hasSlide }) => (hasSlide ? '1fr' : '1fr 1fr')};
  gap: 8px;
`;

const StyledLibraryZone = styled(Flex)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
`;

const StyledMappedTagLabel = styled(Flex)`
  background-color: rgba(100, 100, 100, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100vw;
  font-size: 12px;
  padding: 4px 16px;
  cursor: pointer;
  gap: 12px;
  width: fit-content;
`;

const TruncateMultiline = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 7;
  -webkit-box-orient: vertical;
  word-break: break-all;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  font-size: 14px;
  padding: 16px 24px 16px 24px;
  max-height: 155px;
`;

type TriggerLabel = {
  readonly value: SensorType;
  readonly label: React.ReactNode;
};

const options: readonly TriggerLabel[] = [
  {
    value: SensorType.NEXMO_BUTTON,
    label: 'Button',
  },
  {
    value: SensorType.NEXMO_NFC,
    label: 'NFC Reader',
  },
  {
    value: SensorType.NEXMO_PICKUP,
    label: 'Pickup',
  },
  {
    value: SensorType.NEXMO_PRESENCE_XY240,
    label: 'Presence',
  },
  {
    value: SensorType.TH_PUSH,
    label: 'Trihow Push',
  },
  {
    value: SensorType.UHF_NEO_DESKTOP,
    label: 'UHF Reader',
  },
  {
    value: SensorType.TOUCH,
    label: 'Touch',
  },
  {
    value: SensorType.GENERIC,
    label: 'Generic',
  },
  {
    value: SensorType.TH_LINK,
    label: 'TH Link',
  },
  {
    value: SensorType.HOME,
    label: 'HOME',
  },
];

type Props = {
  readonly slide: FrontendSlide | null;
  readonly slideGroupId?: string;
  readonly slidePosition?: number;
  readonly magicModels: MagicModel[];
  readonly languageSupport: boolean;
  readonly disallowSlideDeletion: boolean;
  readonly channelSelective: boolean;
  readonly isFirst?: boolean;
  readonly isLast?: boolean;
  readonly isTrihowPad?: boolean;
  readonly isDesignatedSplitScreen?: boolean;
  readonly images: FrontendSlide[];
  readonly videos: FrontendSlide[];
  readonly webslides: FrontendSlide[];
  readonly setImages: React.Dispatch<React.SetStateAction<FrontendSlide[]>>;
  readonly setVideos: React.Dispatch<React.SetStateAction<FrontendSlide[]>>;
  readonly setWebslides: React.Dispatch<React.SetStateAction<FrontendSlide[]>>;
  readonly onClick: (slide: FrontendSlide | null) => void;
  readonly onChangeNameInLibrary: (slideId: string, newName: string) => void;
  readonly onUpdateSlide: (
    slideId: string | null,
    slideData: Partial<FrontendSlide> | null,
    isFromLibrary?: boolean,
    slidePosition?: number,
  ) => void;
  readonly onUpdateSlideOrder: (
    slideGroupId?: string,
    order?: 'right' | 'left',
    slideIndex?: number,
  ) => void;
  readonly onVideoOptionsChange: (
    groupId: string,
    sliderId: string,
    videoOptions: SlideVideoOptions,
  ) => void;
};

export const EditorSlideComponent = ({
  slide,
  slideGroupId,
  slidePosition,
  magicModels,
  languageSupport,
  disallowSlideDeletion,
  channelSelective,
  isFirst,
  isLast,
  isTrihowPad = false,
  isDesignatedSplitScreen = false,
  images,
  videos,
  webslides,
  setImages,
  setVideos,
  setWebslides,
  onClick,
  onChangeNameInLibrary,
  onUpdateSlide,
  onUpdateSlideOrder,
  onVideoOptionsChange,
}: Props) => {
  // will be implemented to work soon
  const hasSplitscreenTrigger =
    slide?.structureData && slide?.structureData.triggerIds
      ? slide?.structureData.triggerIds.some((item) =>
          item.type.toUpperCase().includes('SPLITSCREEN'),
        )
      : false;

  const [slideName, setSlideName] = useState(slide?.name ?? '');
  const { isAdmin } = useUserInfo();
  const [selectedCustomerId] = useSelectedCustomerId();
  const slideRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const intl = useIntl();

  const [isReplacing, setIsReplacing] = useState<FrontendSlide | null>(null);

  const [slideSplitScreenToggle, setSlideSplitScreenToggle] = useState(
    slide?.splitScreen || false,
  );
  const [replaceWebslide, setReplaceWebslide] = useState(false);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSlideLibraryOpen, setIsSlideLibraryOpen] = useState(false);

  const [sidebarSelectedOption, setSidebarSelectedOption] =
    useState<SensorType | null>(SensorType.NEXMO_NFC);

  const shouldShowWebslideOption =
    !isTrihowPad || (isTrihowPad && !isDesignatedSplitScreen);

  const shouldShowDelete =
    isTrihowPad ||
    isAdmin ||
    (!(
      slide?.structureData?.triggerIds &&
      slide.structureData.triggerIds.length > 0
    ) &&
      !disallowSlideDeletion);

  useEffect(() => {
    if (slide?.name) {
      setSlideName(slide?.name);
    }
  }, [slide?.name]);

  useEffect(() => {
    if (!isSlideLibraryOpen) {
      setIsReplacing(null);
    }
  }, [isSlideLibraryOpen]);

  const componentId = slide === null ? 'no-slide' : slide.slideId;
  return (
    <StyledSlideWrapper
      data-testid={`editor-slide-component-wrapper-${componentId}`}
      isSidebarOpen={isSidebarOpen}
      ref={slideRef}
      isTrihowPad={isTrihowPad}
    >
      <StyledTopSlideBar>
        <div
          style={{
            position: 'absolute',
            zIndex: '10',
            top: '0',
            right: '0',
          }}
        >
          {slide ? (
            <KebapMenu
              testId={`editor-slide-component-kebab-menu-${componentId}`}
            >
              {!isTrihowPad && (
                <MenuItem
                  icon={<IconEdit />}
                  onClick={() => {
                    setIsSidebarOpen(true);
                  }}
                >
                  <T body1>
                    <FormattedMessage defaultMessage="Edit" id="wEQDC6" />
                  </T>
                </MenuItem>
              )}
              {shouldShowDelete ? (
                <MenuItem
                  icon={<IconTrash />}
                  onClick={() => {
                    onUpdateSlide(slide.slideId ?? null, null);
                  }}
                >
                  <T body1>
                    <FormattedMessage defaultMessage="Delete" id="K3r6DQ" />
                  </T>
                </MenuItem>
              ) : null}
              <MenuItem
                icon={<IconView />}
                onClick={async () => {
                  const file = await triggerFileUploadDialog(toast, intl);

                  if (file) {
                    const reader = new FileReader();
                    reader.readAsDataURL(file);

                    reader.onload = () => {
                      if (typeof reader.result === 'string') {
                        onUpdateSlide(
                          slide?.slideId ?? null,
                          {
                            ...slide,
                            index: slide.index,
                            asset: reader.result,
                            name: file.name || slide.name,
                            contentType: file.type,
                            size: file.size,
                            file,
                          },
                          false,
                          slidePosition,
                        );
                      }
                    };

                    reader.onerror = () => {
                      console.error('could not parse file', reader);
                    };
                  }
                }}
              >
                <T body1>
                  <FormattedMessage
                    defaultMessage="Replace with Upload"
                    id="uUgF3n"
                  />
                </T>
              </MenuItem>
              {shouldShowWebslideOption && (
                <MenuItem
                  icon={<IconLink />}
                  onClick={() => {
                    setReplaceWebslide(true);
                  }}
                  disabled={hasSplitscreenTrigger}
                  tooltip={
                    hasSplitscreenTrigger ? (
                      <FormattedMessage
                        defaultMessage="Not for splitscreen"
                        id="RDpLWx"
                      />
                    ) : undefined
                  }
                >
                  <T body1>
                    <FormattedMessage
                      defaultMessage="Replace with Webslide"
                      id="HEXQDO"
                    />
                  </T>
                </MenuItem>
              )}
              <MenuItem
                icon={<IconView />}
                onClick={() => {
                  setIsSlideLibraryOpen(true);
                  setIsReplacing(slide);
                }}
              >
                <T body1>
                  <FormattedMessage
                    defaultMessage="Replace from Library"
                    id="5IT9IZ"
                  />
                </T>
              </MenuItem>
              {!isDesignatedSplitScreen && (
                <MenuItem
                  icon={<IconArrowLeft />}
                  disabled={isFirst}
                  onClick={async () => {
                    if (!isFirst) {
                      onUpdateSlideOrder(slideGroupId, 'left', slide.index);
                    }
                  }}
                >
                  <T body1>
                    <FormattedMessage defaultMessage="Move left" id="2ybHqn" />
                  </T>
                </MenuItem>
              )}
              {!isDesignatedSplitScreen && (
                <MenuItem
                  disabled={isLast}
                  icon={<IconArrowRight />}
                  onClick={async () => {
                    if (!isLast) {
                      onUpdateSlideOrder(slideGroupId, 'right', slide.index);
                    }
                  }}
                >
                  <T body1>
                    <FormattedMessage defaultMessage="Move right" id="rZ1B/O" />
                  </T>
                </MenuItem>
              )}
            </KebapMenu>
          ) : (
            shouldShowWebslideOption && (
              <div
                style={{ cursor: 'pointer', zIndex: '1' }}
                onClick={() => setReplaceWebslide(true)}
              >
                <IconLink />
              </div>
            )
          )}
        </div>
        <Flex direction="column" grow>
          <StyledSlideName slide={Boolean(slide)} hasName={Boolean(slideName)}>
            {slide?.name ? (
              <input
                onBlur={(event) => {
                  if (
                    slide?.slideId &&
                    slide.name !== event.target.value &&
                    event.target.value !== ''
                  ) {
                    onUpdateSlide(slide.slideId, {
                      name: event.target.value,
                    });
                  }
                }}
                onChange={(event) => setSlideName(event.target.value)}
                onKeyDown={(event) =>
                  event.key === 'Enter' && event.currentTarget.blur()
                }
                value={slideName}
              />
            ) : null}
          </StyledSlideName>
          {slide ? (
            <SensorMappingSidebar
              slideSplitScreenToggle={slideSplitScreenToggle}
              setSlideSplitScreenToggle={setSlideSplitScreenToggle}
              channelSelective={channelSelective}
              languageSupport={languageSupport}
              isOpen={Boolean(isSidebarOpen)}
              onClose={() => setIsSidebarOpen(false)}
              magicModels={magicModels}
              onUpdateSlide={(slideData: Partial<FrontendSlide> | null) =>
                slide.slideId
                  ? onUpdateSlide(slide.slideId, slideData)
                  : undefined
              }
              selectedType={sidebarSelectedOption}
              setSelectedType={setSidebarSelectedOption}
              slide={slide}
              subtitle={slideName}
              isLoopVideoEnabled={slide?.videoOptions?.loop ?? false}
              onChangeIsLoopVideo={(isLoop) => {
                onVideoOptionsChange(
                  slide.slideGroupId ?? '',
                  slide.slideId ?? '',
                  {
                    autoPlay: true,
                    loop: isLoop,
                  },
                );
              }}
            />
          ) : null}

          {isSlideLibraryOpen && (
            <SlideLibrary
              isReplacing={isReplacing}
              onChangeNameInLibrary={onChangeNameInLibrary}
              disallowSlideDeletion={disallowSlideDeletion}
              disableWebslides={
                hasSplitscreenTrigger || isDesignatedSplitScreen
              }
              onUpdateSlide={onUpdateSlide}
              images={images}
              videos={videos}
              webslides={webslides}
              setImages={setImages}
              setVideos={setVideos}
              setWebslides={setWebslides}
              setIsSlideLibraryOpen={setIsSlideLibraryOpen}
              slidePosition={slidePosition}
            />
          )}

          <Flex gap="8px 8px" wrap="wrap">
            {slide?.structureData?.triggerIds.length
              ? slide?.structureData?.triggerIds.map(({ type }) => {
                  const shouldShow =
                    !isTrihowPad &&
                    (isAdmin ||
                      (options?.find((option) => option.value === type)
                        ?.label !== undefined &&
                        (type === SensorType.NEXMO_NFC ||
                          type === SensorType.TH_PUSH)));
                  return shouldShow ? (
                    <div
                      key={type}
                      onClick={() => {
                        setSidebarSelectedOption(
                          type === SensorType.SPLITSCREEN
                            ? SensorType.HOME
                            : (type as SensorType),
                        );
                        setIsSidebarOpen(true);
                      }}
                      style={{ marginBottom: '8px' }}
                    >
                      <StyledMappedTagLabel>
                        <T>
                          {options?.find((option) => option.value === type)
                            ?.label ??
                            type.replace('_', ' ').replace('NEXMO ', '')}
                        </T>
                        <span style={{ transform: 'scale(1.2)' }}>
                          <IconEdit />
                        </span>
                      </StyledMappedTagLabel>
                    </div>
                  ) : null;
                })
              : null}
          </Flex>
          <StyledDropzone
            hasSlide={Boolean(slide)}
            onClick={() => {
              if (slide) {
                onClick(slide);
              }
            }}
          >
            {slide?.contentType === 'text/html' ? (
              <TruncateMultiline title={slide?.asset}>
                {slide?.asset}
              </TruncateMultiline>
            ) : (
              <>
                <DropFile
                  lazyLoading={!(slideGroupId === 'home-group')}
                  file={
                    slide
                      ? {
                          slideId: slide.slideId,
                          contentType: slide.contentType,
                          name: slide.name,
                          asset: slide.asset,
                          size: 0,
                        }
                      : undefined
                  }
                  key={componentId}
                  setFiles={(files) => {
                    onUpdateSlide(
                      slide?.slideId ?? null,
                      {
                        ...slide,
                        index: slidePosition,
                        customer: selectedCustomerId,
                        asset: files[0].asset,
                        name: files[0].name,
                        contentType: files[0].contentType,
                        size: files[0].size,
                        file: files[0].file,
                      },
                      false,
                      slidePosition,
                    );
                  }}
                />
                {!slide && (
                  <StyledLibraryZone
                    data-testid="slide-library-zone"
                    onClick={() => {
                      setIsSlideLibraryOpen(true);
                    }}
                  >
                    <IconBook />
                    <FormattedMessage
                      defaultMessage="Choose from Library"
                      id="DUcguC"
                    />
                  </StyledLibraryZone>
                )}
              </>
            )}
          </StyledDropzone>
        </Flex>
      </StyledTopSlideBar>
      {replaceWebslide && (
        <EnterUrlModal
          setReplaceWebslide={setReplaceWebslide}
          onUpdateSlide={onUpdateSlide}
          slide={
            replaceWebslide
              ? ({ ...slide, asset: '', index: slidePosition } as FrontendSlide)
              : ({
                  ...slide,
                  index: slidePosition,
                } as FrontendSlide)
          }
        />
      )}
    </StyledSlideWrapper>
  );
};
